import { AfterViewInit, Component, ErrorHandler, Inject, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal, computed } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { FieldTree, form, required } from '@angular/forms/signals'

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'

import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'

import { LaskuService } from '../../_angular/service/lasku/lasku.service'

import { Lasku, LaskuBase, LaskuReskontra } from '@lemon/core-firebase/model/lasku'
import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'

export interface LaskuMerkitseOsittainMaksetuksiDialogData {
  juurilasku: Lasku
  kasiteltava: LaskuBase
  reskontra: LaskuReskontra[]
}

interface OsittainMaksettuLomake {
  maksettuMaara: number | null
}

@Component({
  selector: 'lasku-merkitse-osittain-maksetuksi-dialog',
  templateUrl: './lasku.merkitse-osittain-maksetuksi.dialog.html',
  styleUrls: ['./lasku.merkitse-osittain-maksetuksi.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LaskuMerkitseOsittainMaksetuksiDialog implements OnInit, AfterViewInit {
  @ViewChild('maksettuMaaraInput', { read: MatInput, static: true }) maksettuMaaraInput: MatInput

  lomake: FieldTree<OsittainMaksettuLomake>
  private readonly _kieli = toSignal(inject(LemonTranslationService).currentLanguageObservable)
  readonly fieldErrors = computed(() => {
    // Virheviestit käännetään nykyisellä kielellä, joten kielen vaihto muodostaa ne uudelleen
    this._kieli()
    return this.validationService.updateValidationStatus('lasku.merkitse-osittain-maksetuksi', this.lomake)
  })
  kasiteltava: LaskuBase = null
  laskunumero: string
  asiakkaanNimi: string
  avoinna: number = 0
  readonly avoimeksiJaa = computed(() => this.avoinna - (this.lomake.maksettuMaara().value() ?? 0))
  readonly jaaOsittainMaksetuksi = computed(() => this.avoimeksiJaa() >= 0.005)

  private _buttonPushInflight: boolean = false
  private readonly _injector = inject(Injector)

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: LaskuMerkitseOsittainMaksetuksiDialogData,
    private errorHandler: ErrorHandler,
    private dialogRef: MatDialogRef<LaskuMerkitseOsittainMaksetuksiDialog>,
    private validationService: FormValidationService,
    private laskuService: LaskuService,
    private sharedLaskuService: LaskuSharedService,
    private currencyService: CurrencyService,
    private ladataanService: LadataanService,
  ) {
    this.kasiteltava = data.kasiteltava
    const summatJaTila = this.sharedLaskuService.annaLaskunSummatJaTilaLaskulleReskontralla(data.juurilasku, data.reskontra)
    this.avoinna = this.currencyService.muutaBigDecimalRahaksi(summatJaTila.avoinna)
    this.laskunumero = this.sharedLaskuService.annaMuotoiltuLaskunumero(data.juurilasku, data.kasiteltava)
    this.asiakkaanNimi = data.kasiteltava.asiakas?.nimi ?? ''
  }

  ngOnInit() {
    this.lomake = form(
      signal<OsittainMaksettuLomake>({ maksettuMaara: this.avoinna }),
      polku => {
        required(polku.maksettuMaara)
      },
      { injector: this._injector },
    )
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.maksettuMaaraInput) {
        this.maksettuMaaraInput.focus()
      }
    }, 25)
  }

  async lisaaMaksettuaSummaa() {
    if (this._buttonPushInflight) {
      return
    }
    this._buttonPushInflight = true

    this.ladataanService.aloitaLataaminen()
    try {
      const result = await this.laskuService.lisaaReskontraMerkinta(this.data.juurilasku, this.lomake.maksettuMaara().value())
      this.dialogRef.close(result)
    } catch (error) {
      this.errorHandler.handleError(error)
    } finally {
      this.ladataanService.lopetaLataaminen()
      this._buttonPushInflight = false
    }
  }
}
